import client from './api-client';
import { serviceGetProveedores } from './carga-masiva-client';
import {
    CONCEPTO_CAUSAL,
    CONCEPTO_COLOR,
    CONCEPTO_USO,
    CONCEPTO_TIPO_SINIESTRO,
    CONCEPTO_TIPO_VEHICULO,
    PARAMETRO_COMUNAS,
    PARAMETRO_CIUDADES,
    ROLES_RESPONSABLE,
    TIPO_PERSONA_PROVEEDOR
} from '../common/config-ingreso-manual';

const aQuery = (filtro) => btoa(JSON.stringify(filtro));

// Los combos reutilizan los endpoints que ya existen (los mismos de recupero-caso-general): devuelven [{ id, nombre }].
const obtieneLista = async (ruta, filtro) => {
    const response = await client.getImplicado(`${ruta}?q=${aQuery(filtro)}`);
    if (response.status === 200 && response.data.code === 1) {
        return response.data.object || [];
    }
    throw new Error(response.data.message);
};

export const getTiposSiniestro = () => obtieneLista('detalles', { concepto: CONCEPTO_TIPO_SINIESTRO });
export const getTiposVehiculo = () => obtieneLista('detalles', { concepto: CONCEPTO_TIPO_VEHICULO });
export const getCausales = () => obtieneLista('detalles', { concepto: CONCEPTO_CAUSAL });
export const getColores = () => obtieneLista('detalles', { concepto: CONCEPTO_COLOR });
export const getUsos = () => obtieneLista('detalles', { concepto: CONCEPTO_USO });
export const getCompanias = () => obtieneLista('companias', {});
export const getMarcas = (idOrgCaso) => obtieneLista('marcas', { idOrgCaso });
export const getComunas = (rutCia) => obtieneLista('generales', { rutCia, parametro: PARAMETRO_COMUNAS });
export const getCiudades = (rutCia) => obtieneLista('generales', { rutCia, parametro: PARAMETRO_CIUDADES });

// Abogados responsables: mismo listado que "Actualiza / Reasigna Recupero" (proveedores por rol).
export const getResponsables = async (rutOrganizacion) => {
    const resultados = [];
    for (const rol of ROLES_RESPONSABLE) {
        try {
            const query = aQuery({ idOrganizacion: rutOrganizacion, rol, tipoPersona: TIPO_PERSONA_PROVEEDOR });
            const lista = await serviceGetProveedores(query);
            if (Array.isArray(lista)) resultados.push(...lista);
        } catch (error) {
            console.error(`Error obteniendo proveedores para el rol ${rol}:`, error);
        }
    }
    // Un mismo abogado puede tener más de un rol: se deja una sola vez.
    return resultados.filter((r, i) => resultados.findIndex((x) => String(x.id) === String(r.id)) === i);
};

// ── Lambda recupero-fnc-ingreso-manual-intercia ─────────────────────────────────────────────────
// 🔴 La ruta aún no existe en API Gateway (hoy el lambda solo se invoca directo): hay que crearla bajo /api
// en el gateway que usa este módulo antes de probar la pantalla.
const RUTA_INGRESO_MANUAL = 'ingreso-caso/manual-intercia';

// Modelos de una marca (cod_marca → [{ id: cod_modelo, nombre }]). No hay un endpoint de modelos en los gateways, así que
// es una acción más del lambda de ingreso manual (no escribe nada).
export const getModelos = async (codMarca) => {
    const response = await client.post(RUTA_INGRESO_MANUAL, { accion: 'modelos', codMarca });
    if (response.status === 200 && response.data.code === 1) {
        return response.data.object || [];
    }
    throw new Error(response.data.message);
};

// Modos de ingreso que el usuario tiene por rol: { modos: ['RECUPERO', 'DEFENSA'] }. Si el servidor aún no conoce la acción
// (lambda sin actualizar), se asume solo Recupero, que es el comportamiento anterior.
export const getConfiguracion = async () => {
    try {
        const response = await client.post(RUTA_INGRESO_MANUAL, { accion: 'configuracion' });
        if (response.status === 200 && response.data.code === 1 && response.data.object && Array.isArray(response.data.object.modos)) {
            return response.data.object;
        }
        if (response.data && /no soportada/i.test(String(response.data.message))) {
            return { modos: ['RECUPERO'] };
        }
        throw new Error(response.data ? response.data.message : 'Respuesta inválida');
    } catch (error) {
        console.error(error);
        throw error;
    }
};

// ¿Ya hay un caso con este siniestro, patente y tipo de gestión? { existe, idCaso }. Es una ayuda: si falla, el servidor lo vuelve a
// validar al revisar y al confirmar.
export const existeCaso = async (datos) => {
    const response = await client.post(RUTA_INGRESO_MANUAL, { ...datos, accion: 'existe' });
    if (response.status === 200 && response.data.code === 1) {
        return response.data.object;
    }
    throw new Error(response.data.message);
};

// Datos de un caso con los nombres de campo del formulario, para precargarlo en modo edición: { idCaso, idResponsable, valores }.
export const obtenerCaso = async (datos) => {
    const response = await client.post(RUTA_INGRESO_MANUAL, { ...datos, accion: 'obtener' });
    if (response.status === 200 && response.data.code === 1) {
        return response.data.object;
    }
    throw new Error(response.data.message);
};

// "Revisar": el servidor valida sin escribir. Devuelve { errores: [{ campo, mensaje }] }.
export const validarIngreso = async (datos) => {
    const response = await client.post(RUTA_INGRESO_MANUAL, { ...datos, accion: 'validar' });
    if (response.status === 200 && response.data.code === 1) {
        return response.data.object;
    }
    throw new Error(response.data.message);
};

// "Confirmar ingreso": crea el caso. Devuelve { folio, responsable, ... }.
export const crearIngreso = async (datos) => {
    const response = await client.post(RUTA_INGRESO_MANUAL, { ...datos, accion: 'crear' });
    if (response.status === 200 && response.data.code === 1) {
        return response.data.object;
    }
    const error = new Error(response.data.message);
    error.errores = response.data.object ? response.data.object.errores : null;
    throw error;
};

// "Confirmar actualización" (modo edición): actualiza el caso existente; no cambia responsable ni bandeja. Devuelve { idCaso, ... }.
export const actualizarIngreso = async (datos) => {
    const response = await client.post(RUTA_INGRESO_MANUAL, { ...datos, accion: 'actualizar' });
    if (response.status === 200 && response.data.code === 1) {
        return response.data.object;
    }
    const error = new Error(response.data.message);
    error.errores = response.data.object ? response.data.object.errores : null;
    throw error;
};

// URL firmada (temporal) de la carta de cobro adjunta, para verla en otra pestaña. La carta vive en el implicado 1 (el tercero).
// Mismo endpoint y mismos parámetros que usa la pantalla de aprobación de pago a CIA tercero (get-file, mode: 'url').
export const getUrlCarta = async ({ idCaso, idCompania, idfolioCarga }) => {
    const q = aQuery({ idCaso, idImplicadoCaso: 1, idCompania: String(idCompania), idfolioCarga, mode: 'url' });
    const response = await client.getDocumento(`get-file?q=${q}`);
    if (response.status === 200 && response.data.code === 1 && response.data.url) {
        return response.data.url;
    }
    throw new Error((response.data && response.data.message) || 'No se pudo obtener la carta.');
};

